import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { AlertTriangle, KeyRound, Receipt, Search, Shield, Users, Wallet } from "lucide-react";

// Imports raw de los .md — Vite los inyecta como string al bundle.
// Si un .md se modifica, basta con rebuild del backoffice.
import inquiriesIncidentPlaybookMd from "./content/INCIDENT_PLAYBOOK_MP.md?raw";
import inquiriesSaludMpOpsMd from "./content/SALUD_MP_OPS.md?raw";
import inquiriesMpPendingMd from "./content/MP_PENDING_IMPROVEMENTS.md?raw";
import settlementFlowOpsMd from "./content/SETTLEMENT_FLOW_OPS.md?raw";
import securityNetworkOpsMd from "./content/SECURITY_NETWORK_OPS.md?raw";
import securityProducerGuideMd from "./content/SECURITY_PRODUCER_GUIDE.md?raw";
import securityStaffGuideMd from "./content/SECURITY_STAFF_GUIDE.md?raw";
import producersMgmtGuideMd from "./content/PRODUCERS_MGMT_GUIDE.md?raw";
import onboardingInvitationsMd from "./content/ONBOARDING_INVITATIONS.md?raw";
import reclutadoresGuideMd from "./content/RECLUTADORES_GUIDE.md?raw";
import rolesMatrixMd from "./content/ROLES_MATRIX.md?raw";
import negocioSuscripcionesMd from "./content/NEGOCIO_SUSCRIPCIONES.md?raw";
import negocioConsumosMd from "./content/NEGOCIO_CONSUMOS_RESULTADO.md?raw";
import negocioPortadaMd from "./content/NEGOCIO_PORTADA.md?raw";

import { cn } from "@/lib/utils/cn";

interface DocEntry {
  slug: string;
  title: string;
  description: string;
  content: string;
  /**
   * Nombre del archivo .md original. Lo usamos para resolver links
   * cross-doc dentro del markdown (ej. `[X](SECURITY_STAFF_GUIDE.md)`
   * → cambiar al doc con filename=SECURITY_STAFF_GUIDE.md).
   */
  filename: string;
}

interface DocGroup {
  title: string;
  icon: React.ReactNode;
  tone: "danger" | "warning" | "info" | "neutral";
  description: string;
  docs: DocEntry[];
}

const GROUPS: DocGroup[] = [
  {
    title: "Negocio · Monetización",
    icon: <Receipt size={14} />,
    tone: "info",
    description: "Para socios: qué se cobra hoy y cómo. No es técnico.",
    docs: [
      {
        slug: "negocio-portada",
        title: "La Portada de mixcore.club",
        description: "Qué muestra la portada, la estrategia de cada sección y cómo se monetiza (eventos destacados, spotlights, Revista). Para negocio.",
        content: negocioPortadaMd,
        filename: "NEGOCIO_PORTADA.md",
      },
      {
        slug: "negocio-suscripciones",
        title: "Suscripciones y cobros (catálogo)",
        description: "Qué se puede cobrar hoy, a quién, a qué precio y cómo funciona el cobro. La foto de negocio.",
        content: negocioSuscripcionesMd,
        filename: "NEGOCIO_SUSCRIPCIONES.md",
      },
      {
        slug: "negocio-consumos",
        title: "Pago de consumos (#23)",
        description: "Qué quedó listo para cobrar los consumos + el 4%, por qué, cómo funciona y qué falta para encenderlo.",
        content: negocioConsumosMd,
        filename: "NEGOCIO_CONSUMOS_RESULTADO.md",
      },
    ],
  },
  {
    title: "Runbooks de emergencia",
    icon: <AlertTriangle size={14} />,
    tone: "danger",
    description: "Qué hacer cuando algo se está rompiendo en producción.",
    docs: [
      {
        slug: "incident-playbook-mp",
        title: "Incident Playbook MercadoPago",
        description: "Runbook 3am: webhooks caídos, pagos sin tickets, MP no procesa.",
        content: inquiriesIncidentPlaybookMd,
        filename: "INCIDENT_PLAYBOOK_MP.md",
      },
      {
        slug: "salud-mp-ops",
        title: "Salud MP — Operativa diaria",
        description: "Cómo usar las cards de Salud MP: forzar sweep, listar pendientes, reconciliar por TX ID.",
        content: inquiriesSaludMpOpsMd,
        filename: "SALUD_MP_OPS.md",
      },
      {
        slug: "mp-pending-improvements",
        title: "Mejoras MP pendientes",
        description: "Deuda consciente del flow MP con triggers medibles para cada mejora.",
        content: inquiriesMpPendingMd,
        filename: "MP_PENDING_IMPROVEMENTS.md",
      },
    ],
  },
  {
    title: "Liquidaciones",
    icon: <Wallet size={14} />,
    tone: "info",
    description: "Operativa del módulo Liquidaciones — el más delicado.",
    docs: [
      {
        slug: "settlement-flow-ops",
        title: "Settlement Flow Ops",
        description: "Flow híbrido: aceptación tácita 3 días, disputas, transferencias, comprobantes.",
        content: settlementFlowOpsMd,
        filename: "SETTLEMENT_FLOW_OPS.md",
      },
    ],
  },
  {
    title: "Guías de Nexus",
    icon: <Users size={14} />,
    tone: "neutral",
    description: "Cómo usar los tableros del back-office.",
    docs: [
      {
        slug: "producers-mgmt-guide",
        title: "Tablero Productores",
        description: "Cómo gestionar cuentas, clubs, eventos y staff. Incluye guía del hard-delete y permisos por rol.",
        content: producersMgmtGuideMd,
        filename: "PRODUCERS_MGMT_GUIDE.md",
      },
      {
        slug: "onboarding-invitations",
        title: "Invitaciones directas",
        description: "Cómo invitar a un artista/productor/proveedor (uno o varios perfiles) sin postulación: enviar desde Seguimiento → Invitaciones, qué datos confirma la persona, estados y reglas.",
        content: onboardingInvitationsMd,
        filename: "ONBOARDING_INVITATIONS.md",
      },
      {
        slug: "reclutadores-guide",
        title: "Programa de Reclutadores",
        description: "Registrar reclutadores, asignar productores, revisar el checklist de cada evento, cómo se calcula la comisión y cómo se liquida y paga cada mes.",
        content: reclutadoresGuideMd,
        filename: "RECLUTADORES_GUIDE.md",
      },
    ],
  },
  {
    title: "Roles y permisos",
    icon: <KeyRound size={14} />,
    tone: "info",
    description: "Jerarquía Owner / Gerente / Administrador + roles operativos del Club.",
    docs: [
      {
        slug: "roles-matrix",
        title: "Matriz de roles del Club (Hub & Staff)",
        description:
          "Quién puede qué en el Hub y el Staff. Actualizada 29/may/2026: el Administrador ahora se asigna por evento, no por club.",
        content: rolesMatrixMd,
        filename: "ROLES_MATRIX.md",
      },
    ],
  },
  {
    title: "Seguridad y blacklist",
    icon: <Shield size={14} />,
    tone: "warning",
    description: "3 niveles (club / cross-club / global) y red Mixcore.",
    docs: [
      {
        slug: "security-network-ops",
        title: "Security Network Ops",
        description: "Operación de la red de seguridad: retención, escalado, apelaciones.",
        content: securityNetworkOpsMd,
        filename: "SECURITY_NETWORK_OPS.md",
      },
      {
        slug: "security-producer-guide",
        title: "Guía para productores",
        description: "Cómo se le explica al productor el sistema desde el Hub.",
        content: securityProducerGuideMd,
        filename: "SECURITY_PRODUCER_GUIDE.md",
      },
      {
        slug: "security-staff-guide",
        title: "Guía para staff (puerta)",
        description: "Cómo usa el scanner el guardia y qué hacer ante un rechazo.",
        content: securityStaffGuideMd,
        filename: "SECURITY_STAFF_GUIDE.md",
      },
    ],
  },
];

const TONE_COLORS: Record<DocGroup["tone"], string> = {
  danger: "text-danger",
  warning: "text-warning",
  info: "text-info",
  neutral: "text-fg-subtle",
};

export function DocsScreen() {
  const allDocs = useMemo(() => GROUPS.flatMap((g) => g.docs.map((d) => ({ ...d, groupTitle: g.title }))), []);
  /**
   * Mapa filename (case-insensitive) → slug. Lo usamos en el custom
   * `<a>` para resolver links cross-doc tipo `[X](SECURITY_STAFF_GUIDE.md)`.
   * Si el target está embedido, hacemos navegación interna (cambio de
   * doc seleccionado) en vez de dejar que el browser navegue a una
   * URL inexistente y caiga en el 404.
   */
  const filenameToSlug = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of allDocs) {
      map.set(d.filename.toLowerCase(), d.slug);
    }
    return map;
  }, [allDocs]);

  const [selectedSlug, setSelectedSlug] = useState<string>(allDocs[0]?.slug ?? "");
  const [search, setSearch] = useState("");

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return GROUPS;
    const q = search.toLowerCase();
    return GROUPS.map((g) => ({
      ...g,
      docs: g.docs.filter(
        (d) =>
          d.title.toLowerCase().includes(q) ||
          d.description.toLowerCase().includes(q) ||
          d.content.toLowerCase().includes(q),
      ),
    })).filter((g) => g.docs.length > 0);
  }, [search]);

  const selected = allDocs.find((d) => d.slug === selectedSlug) ?? allDocs[0];

  // Cuando el filtro deja vacío el grupo del seleccionado, saltar al primero visible
  useEffect(() => {
    if (!search.trim()) return;
    const visible = filteredGroups.flatMap((g) => g.docs);
    if (visible.length > 0 && !visible.find((d) => d.slug === selectedSlug)) {
      setSelectedSlug(visible[0].slug);
    }
  }, [search, filteredGroups, selectedSlug]);

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header>
        <p className="nx-eyebrow mb-1">Docs</p>
        <h1 className="nx-h1">Documentación operativa</h1>
        <p className="text-sm text-fg-muted mt-1">
          Runbooks y guías para operar Mixcore. Lectura completa dentro del
          portal — no se exponen URLs externas.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-4 items-start">
        {/* Sidebar */}
        <aside className="nx-card p-3 lg:sticky lg:top-4 lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto flex flex-col gap-3">
          <div className="relative">
            <Search
              size={12}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar en docs…"
              className="nx-input pl-7 text-xs"
            />
          </div>

          {filteredGroups.length === 0 ? (
            <div className="text-2xs text-fg-subtle text-center py-4">
              Sin resultados para "{search}".
            </div>
          ) : (
            filteredGroups.map((g) => (
              <section key={g.title}>
                <div className="flex items-center gap-1.5 mb-1.5 px-1">
                  <span className={TONE_COLORS[g.tone]}>{g.icon}</span>
                  <h3 className="text-2xs uppercase tracking-wider text-fg-subtle font-semibold">
                    {g.title}
                  </h3>
                </div>
                <div className="flex flex-col gap-0.5">
                  {g.docs.map((doc) => (
                    <button
                      key={doc.slug}
                      onClick={() => setSelectedSlug(doc.slug)}
                      className={cn(
                        "text-left rounded-md px-2.5 py-1.5 transition-colors text-xs border",
                        selectedSlug === doc.slug
                          ? "bg-accent-soft border-accent text-fg"
                          : "border-transparent hover:bg-bg-hover text-fg-muted",
                      )}
                    >
                      <div className="font-medium truncate">{doc.title}</div>
                    </button>
                  ))}
                </div>
              </section>
            ))
          )}
        </aside>

        {/* Viewer */}
        <main className="nx-card p-6 lg:p-8 min-h-[calc(100vh-200px)]">
          {selected ? (
            <article className="prose-doc">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ href, children, ...rest }) => {
                    if (!href) return <span {...rest}>{children}</span>;

                    // Link externo (http/https) → abre en nueva pestaña.
                    if (/^https?:\/\//i.test(href)) {
                      return (
                        <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
                          {children}
                        </a>
                      );
                    }

                    // Ancla (#section) — déjala fluir al browser para scroll.
                    if (href.startsWith("#")) {
                      return (
                        <a href={href} {...rest}>
                          {children}
                        </a>
                      );
                    }

                    // Mailto / tel → comportamiento default.
                    if (/^(mailto:|tel:)/i.test(href)) {
                      return (
                        <a href={href} {...rest}>
                          {children}
                        </a>
                      );
                    }

                    // Link relativo a un .md → resolver contra el catálogo embedido.
                    // El filename puede venir con prefix (`./X.md`, `../X.md`)
                    // o sin él (`X.md`). Sacamos el basename.
                    const basename = href.split(/[\\/]/).pop() ?? href;
                    if (/\.md(#.*)?$/i.test(basename)) {
                      const pure = basename.split("#")[0].toLowerCase();
                      const targetSlug = filenameToSlug.get(pure);
                      if (targetSlug) {
                        return (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setSelectedSlug(targetSlug);
                              // Scroll arriba para que se vea el header del doc nuevo.
                              window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                            {...rest}
                          >
                            {children}
                          </a>
                        );
                      }
                      // Link a doc NO embedido (ej. `../src/modules/security/README.md`).
                      // En vez de romper la navegación, lo mostramos como
                      // referencia inactiva con tooltip.
                      return (
                        <span
                          className="text-fg-muted underline decoration-dotted cursor-help"
                          title={`Referencia interna no embedida: ${href}`}
                        >
                          {children}
                        </span>
                      );
                    }

                    // Cualquier otro link relativo (paths a source code, etc.)
                    // → desactivar para no romper la navegación del SPA.
                    return (
                      <span
                        className="text-fg-muted underline decoration-dotted cursor-help"
                        title={`Referencia interna no embedida: ${href}`}
                      >
                        {children}
                      </span>
                    );
                  },
                }}
              >
                {selected.content}
              </ReactMarkdown>
            </article>
          ) : (
            <div className="text-fg-subtle text-sm text-center py-12">
              Seleccioná un documento de la izquierda.
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
